<script setup lang="ts">
import { activeMenuTarget, flattenNavigation, isNavBranch, menuGroupsForCapabilities } from '~/utils/adminNavigation'
import type { NavBranch, NavGroup, NavLeaf } from '~/utils/adminNavigation'

const route = useRoute()
const config = useRuntimeConfig()

/**
 * Le menu est monté deux fois : en colonne sur bureau, et dans le panneau
 * superposé en mobile. `scrollable` distingue la première, seule à défiler pour
 * son propre compte ; dans le panneau, c'est le conteneur superposé qui défile.
 */
const props = withDefaults(defineProps<{ scrollable?: boolean }>(), { scrollable: false })

/*
 * Seule l'instance bureau défile pour son propre compte. Celle du panneau
 * mobile est portée par un conteneur qui, lui, défile — branché depuis le
 * layout sous la clé `mobile`. On l'isole ici sous une troisième clé, sans quoi
 * son relevé automatique au démontage écraserait avec un zéro la position de
 * l'un ou l'autre.
 */
const { el: scrollEl, remember } = useSidebarScroll(props.scrollable ? 'desktop' : 'inutilise')

/*
 * `scrollable` est lu une fois, au montage : la clé est donc figée pour la vie
 * de l'instance. C'est volontaire — les deux emplacements passent une valeur
 * littérale, et la bascule bureau/mobile se joue en CSS (`hidden lg:block`),
 * pas en liant la prop à un point de rupture. La lier dynamiquement demanderait
 * de dériver la clé dans un `computed`.
 */

// --- État persisté ---

const collapsedGroups = ref<Record<string, boolean>>({})
const expandedSubmenus = ref<Record<string, boolean>>({})

if (import.meta.client) {
  const savedGroups = localStorage.getItem('admin-sidebar-collapsed')
  if (savedGroups) {
    try { collapsedGroups.value = JSON.parse(savedGroups) } catch { /* préférence illisible : on garde les valeurs par défaut */ }
  }
  const savedSubs = localStorage.getItem('admin-sidebar-submenus')
  if (savedSubs) {
    try { expandedSubmenus.value = JSON.parse(savedSubs) } catch { /* préférence illisible : on garde les valeurs par défaut */ }
  }
}

function toggleGroup(key: string) {
  collapsedGroups.value[key] = !collapsedGroups.value[key]
  if (import.meta.client) {
    localStorage.setItem('admin-sidebar-collapsed', JSON.stringify(collapsedGroups.value))
  }
}

function toggleSubmenu(key: string) {
  expandedSubmenus.value[key] = !expandedSubmenus.value[key]
  if (import.meta.client) {
    localStorage.setItem('admin-sidebar-submenus', JSON.stringify(expandedSubmenus.value))
  }
}

// --- Menu ---

/**
 * Couleurs d'accent par groupe.
 *
 * L'espace adhérent se distingue en violet, dans la barre latérale comme sur le
 * tableau de bord : la même couleur signale le même périmètre, et l'on sait
 * d'un coup d'œil si l'on édite le site public ou l'espace réservé.
 *
 * Les classes sont écrites en toutes lettres — Tailwind analyse les sources
 * statiquement, une classe composée à l'exécution ne serait pas générée.
 */
interface GroupAccent {
  /** Fond de l'item actif */
  activeBg: string
  /** Parent d'un sous-menu dont un enfant est actif */
  branch: string
}

/**
 * L'arborescence partagée ne porte que la *clé* de l'accent : les classes
 * Tailwind sont une affaire d'affichage, et la palette de commandes n'en a que
 * faire. Elles se résolvent donc ici, au seul endroit qui les emploie.
 */
const accents: Record<NavGroup['accent'], GroupAccent> = {
  violet: {
    activeBg: 'bg-violet-600 text-white font-medium',
    branch: 'text-violet-600 font-medium',
  },
  primary: {
    activeBg: 'bg-[var(--ui-primary)] text-white font-medium',
    branch: 'text-[var(--ui-primary)] font-medium',
  },
}

const auth = useAuthStore()

/**
 * Menu effectif : les entrées `adminOnly` et `superAdminOnly` disparaissent
 * pour les rôles qui n'y ont pas droit.
 *
 * L'arborescence et son filtre vivent dans `~/utils/adminNavigation`, partagés
 * avec la palette de commandes. Deux listes séparées divergeraient : une entrée
 * ajoutée au menu manquerait à la palette, et un filtre recopié finirait par ne
 * plus correspondre — la palette proposerait alors des écrans répondant 403.
 *
 * Masquer n'est pas protéger — `/seeds` porte le middleware `superadmin-app`,
 * `/sauvegardes` refuse quiconque n'est pas superadministrateur de l'annuaire,
 * et les routes qu'elles appellent vérifient le rôle côté serveur. On évite
 * seulement de proposer une destination qui répondrait 403.
 */
// Ordre et pages système réglés par les super-administrateurs (#332).
const { personnalisation } = useMenuAdministration()

const groupesEffectifs = computed(() => menuGroupsForCapabilities({
  isAdmin: auth.isAdmin,
  isSuperAdmin: auth.isSuperAdmin,
  isGlobalSuperAdmin: auth.isGlobalSuperAdmin,
}, personnalisation.value))

/** Les mêmes groupes, leur clé d'accent résolue en classes pour l'affichage. */
const menuGroups = computed(() =>
  groupesEffectifs.value.map(group => ({ ...group, accent: accents[group.accent] })),
)

// --- Helpers ---

/**
 * Toutes les destinations du menu, sous-menus compris.
 *
 * L'aplatissement est celui de la source partagée : il descend à n'importe
 * quelle profondeur, là où le parcours d'origine s'arrêtait au deuxième niveau
 * et aurait laissé passer une feuille rangée plus bas.
 *
 * Il porte sur l'arbre **du menu**, non sur celui de la palette : les zones de
 * menu n'y figurent pas, et `/menus/header` laisse donc « Menus » actif — le
 * comportement d'avant.
 */
const menuTargets = computed(() => flattenNavigation(groupesEffectifs.value).map(d => d.to))

/** Une seule entrée allumée : la cible la plus précise pour le chemin courant */
/*
 * Un écran peut désigner lui-même son entrée : l'édition de la page d'une
 * cible (`/pages/<id>`) allume « Cibles », non « Pages ».
 */
const menuActifForce = useState<string | null>('menu-actif-force', () => null)
const cibleActive = computed(() => menuActifForce.value ?? activeMenuTarget(route.path, menuTargets.value))

function isActive(to: string) {
  // Les liens fixes de la barre (`/`, `/recherche`) n'appartiennent à aucun
  // groupe : la correspondance exacte suffit à les allumer.
  if (route.path === to) return true
  if (to === '/') return false
  return cibleActive.value === to
}

/**
 * Les enfants qu'un sous-menu affiche : ceux qui mènent quelque part.
 *
 * Le type autorise désormais un enfant sans route — un niveau intermédiaire
 * plus bas dans l'arbre. Le menu n'en compte aucun aujourd'hui, mais le rendre
 * ici produirait un lien vide plutôt qu'une erreur : mieux vaut l'écarter
 * explicitement que le laisser passer.
 */
function sousEntrees(item: NavBranch) {
  return item.children.filter((child): child is NavLeaf & { to: string } => Boolean(child.to))
}

function isSubmenuActive(item: NavBranch) {
  return sousEntrees(item).some(child => isActive(child.to))
}

/**
 * Relever la position au clic, et non dans un hook de navigation : à ce moment
 * l'élément est encore monté et sa valeur exacte, là où un `onBeforeRouteLeave`
 * s'exécuterait alors que le démontage a pu déjà commencer.
 */
function onNavigate() {
  if (props.scrollable) remember()
}

const siteUrl = config.public.sitePublicUrl || '#'

// « Voir le site » : le site connecté en tant qu'admin (#180), voir `useSiteEnAdmin`.
const { ouvrirSurLeSite } = useSiteEnAdmin()
const voirLeSite = () => ouvrirSurLeSite(siteUrl)

// --- Accès rapide ---

const { open: ouvrirPalette } = useCommandPalette()
const { close: fermerPanneauMobile } = useSidebar()

/**
 * En mobile, le menu vit dans un panneau superposé.
 *
 * Le layout ne le referme que sur changement de route : ouvrir la palette n'en
 * change aucune, et le panneau resterait derrière elle. On le referme donc ici,
 * au clic — sans effet sur bureau, où il n'est jamais ouvert.
 */
function ouvrirAccesRapide() {
  fermerPanneauMobile()
  ouvrirPalette()
}

/**
 * Le raccourci affiché, adapté au système.
 *
 * `useKbd` de Nuxt UI détecte macOS — le même mécanisme que celui qui bascule
 * `meta_k` en Ctrl+K côté raccourci — pour que la mention corresponde à la
 * touche qui fonctionne réellement.
 */
const { macOS } = useKbd()
const raccourciPalette = computed(() => macOS.value ? '⌘K' : 'Ctrl K')
</script>

<template>
  <!--
    La largeur est réservée ici, sur le `nav` resté dans le flux : le menu
    lui-même en sort (`fixed`), et sans cette réservation le contenu passerait
    dessous.
  -->
  <nav class="w-full shrink-0 lg:w-56">
    <!--
      Position fixe plutôt que collante.

      `sticky` suppose une course à parcourir dans le parent ; or sur une page
      courte la colonne fait la hauteur du menu, la course est nulle et le menu
      remonte avec la page. `fixed` s'en affranchit : il se cale sous l'en-tête
      (3.5rem plus la marge haute du conteneur) quelle que soit la page.

      Le menu défile alors pour son seul compte, indépendamment de la page :
      une navigation remet le contenu en haut sans l'emmener avec lui.

      `menu-defilant` masque sa barre — le menu déborde dès que les groupes
      sont dépliés, et l'afficher ferait doublon avec celle de la page.
      `overscroll-contain` évite que le défilement se propage à la page une
      fois le menu arrivé en butée.
    -->
    <div
      ref="scrollEl"
      class="flex flex-col gap-1 pb-6"
      :class="props.scrollable
        ? 'menu-defilant fixed top-20 w-56 max-h-[calc(100vh-5rem)] overflow-y-auto overscroll-contain'
        : ''"
    >
      <!-- Dashboard -->
      <NuxtLink
        to="/"
        class="flex items-center gap-2 rounded-md px-3 py-2 text-sm font-medium transition-colors"
        :class="isActive('/')
          ? 'bg-[var(--ui-primary)] text-white'
          : 'text-[var(--ui-text)] hover:bg-[var(--ui-bg-elevated)]'"
        @click="onNavigate"
      >
        <UIcon name="i-lucide-layout-dashboard" class="text-base shrink-0" />
        <span>Tableau de bord</span>
      </NuxtLink>

      <!--
        Recherche transverse.

        Posée après le tableau de bord, et avant l'accès rapide : c'est un
        outil de navigation, non une rubrique — elle ne figure donc dans aucun
        groupe. Les écrans de liste gardent chacun leur propre champ de
        recherche, qui filtre leur table ; celui-ci cherche partout à la fois,
        brouillons compris.

        Distincte de l'accès rapide juste en dessous, et les deux se complètent :
        la palette mène à un **écran** que l'on sait nommer, la recherche à un
        **contenu** dont on ne connaît qu'un mot.
      -->
      <NuxtLink
        to="/recherche"
        class="flex items-center gap-2 rounded-md px-3 py-2 text-sm font-medium transition-colors"
        :class="isActive('/recherche')
          ? 'bg-[var(--ui-primary)] text-white'
          : 'text-[var(--ui-text)] hover:bg-[var(--ui-bg-elevated)]'"
        @click="onNavigate"
      >
        <UIcon name="i-lucide-search" class="text-base shrink-0" />
        <span>Recherche</span>
      </NuxtLink>

      <!--
        Accès rapide (la palette de commandes).

        Sa place est ici, sous le tableau de bord, et non dans l'en-tête :
        l'en-tête porte des icônes nues, un raccourci écrit en toutes lettres
        y détonnerait. Dans le menu, la mention grise à droite se lit comme les
        autres libellés — et c'est là qu'on cherche une destination.

        Un bouton, non un lien : la palette n'a pas d'adresse propre.
      -->
      <button
        class="flex w-full items-center gap-2 rounded-md px-3 py-2 text-sm text-[var(--ui-text-muted)] transition-colors hover:bg-[var(--ui-bg-elevated)] hover:text-[var(--ui-text)]"
        @click="ouvrirAccesRapide"
      >
        <UIcon name="i-lucide-zap" class="text-base shrink-0" />
        <span class="flex-1 text-left">Accès rapide</span>
        <span class="text-xs text-[var(--ui-text-dimmed)]">{{ raccourciPalette }}</span>
      </button>

      <!-- Voir le site -->
      <a
        :href="siteUrl"
        target="_blank"
        rel="noopener"
        class="flex items-center gap-2 rounded-md px-3 py-2 text-sm text-[var(--ui-text-muted)] transition-colors hover:bg-[var(--ui-bg-elevated)] hover:text-[var(--ui-text)]"
        @click.prevent="voirLeSite"
      >
        <UIcon name="i-lucide-external-link" class="text-base shrink-0" />
        <span>Voir le site</span>
      </a>

      <!-- Séparateur -->
      <div class="my-2 border-t border-[var(--ui-border)]" />

      <!-- Groupes -->
      <div v-for="group in menuGroups" :key="group.key" class="mb-1">
        <!-- Header du groupe -->
        <button
          class="flex w-full items-center justify-between px-3 py-1 text-xs font-semibold uppercase tracking-wider text-[var(--ui-text-muted)] hover:text-[var(--ui-text)] transition-colors"
          @click="toggleGroup(group.key)"
        >
          <span>{{ group.label }}</span>
          <UIcon
            name="i-lucide-chevron-down"
            class="text-xs transition-transform"
            :class="collapsedGroups[group.key] ? '-rotate-90' : ''"
          />
        </button>

        <!-- Items du groupe -->
        <ul v-show="!collapsedGroups[group.key]" class="mt-0.5 flex flex-col gap-0.5">
          <li v-for="item in group.items" :key="item.id">
            <!-- Item simple (lien direct) -->
            <NuxtLink
              v-if="!isNavBranch(item)"
              :to="(item as NavLeaf).to"
              :external="(item as NavLeaf).externe"
              :target="(item as NavLeaf).nouvelOnglet ? '_blank' : undefined"
              :rel="(item as NavLeaf).nouvelOnglet ? 'noopener' : undefined"
              class="flex items-center gap-2 rounded-md px-3 py-1.5 text-sm transition-colors"
              :class="isActive((item as NavLeaf).to)
                ? group.accent.activeBg
                : 'text-[var(--ui-text-muted)] hover:bg-[var(--ui-bg-elevated)] hover:text-[var(--ui-text)]'"
              @click="onNavigate"
            >
              <UIcon :name="item.icon" class="text-base shrink-0" />
              <span>{{ item.label }}</span>
            </NuxtLink>

            <!-- Item composé (sous-menu dépliable) -->
            <template v-else>
              <!--
                Sous-menu qui mène aussi quelque part (#339) : le libellé est
                un lien, la flèche seule déplie. Un même clic ne peut pas à la
                fois ouvrir une page et dérouler une liste.
              -->
              <div
                v-if="(item as NavBranch).to"
                class="flex items-center rounded-md text-sm transition-colors"
                :class="isActive((item as NavBranch).to!)
                  ? group.accent.activeBg
                  : isSubmenuActive(item as NavBranch)
                    ? group.accent.branch
                    : 'text-[var(--ui-text-muted)] hover:bg-[var(--ui-bg-elevated)] hover:text-[var(--ui-text)]'"
              >
                <NuxtLink
                  :to="(item as NavBranch).to!"
                  class="flex flex-1 min-w-0 items-center gap-2 px-3 py-1.5"
                  @click="onNavigate"
                >
                  <UIcon :name="item.icon" class="text-base shrink-0" />
                  <span class="flex-1 text-left">{{ item.label }}</span>
                </NuxtLink>
                <button
                  class="shrink-0 px-2 py-1.5"
                  :aria-label="expandedSubmenus[item.id] ? `Replier ${item.label}` : `Déplier ${item.label}`"
                  :aria-expanded="!!expandedSubmenus[item.id]"
                  @click="toggleSubmenu(item.id)"
                >
                  <UIcon
                    name="i-lucide-chevron-right"
                    class="text-xs transition-transform"
                    :class="expandedSubmenus[item.id] ? 'rotate-90' : ''"
                  />
                </button>
              </div>
              <button
                v-else
                class="flex w-full items-center gap-2 rounded-md px-3 py-1.5 text-sm transition-colors"
                :class="isSubmenuActive(item as NavBranch)
                  ? group.accent.branch
                  : 'text-[var(--ui-text-muted)] hover:bg-[var(--ui-bg-elevated)] hover:text-[var(--ui-text)]'"
                @click="toggleSubmenu(item.id)"
              >
                <UIcon :name="item.icon" class="text-base shrink-0" />
                <span class="flex-1 text-left">{{ item.label }}</span>
                <UIcon
                  name="i-lucide-chevron-right"
                  class="text-xs transition-transform"
                  :class="expandedSubmenus[item.id] ? 'rotate-90' : ''"
                />
              </button>

              <!-- Sous-items -->
              <ul v-show="expandedSubmenus[item.id]" class="ml-5 mt-0.5 flex flex-col gap-0.5 border-l border-[var(--ui-border)] pl-2">
                <li v-for="child in sousEntrees(item as NavBranch)" :key="child.to">
                  <NuxtLink
                    :to="child.to"
                    :external="child.externe"
                    :target="child.nouvelOnglet ? '_blank' : undefined"
                    :rel="child.nouvelOnglet ? 'noopener' : undefined"
                    class="flex items-center gap-2 rounded-md px-2 py-1 text-xs transition-colors"
                    :class="isActive(child.to)
                      ? group.accent.activeBg
                      : 'text-[var(--ui-text-muted)] hover:bg-[var(--ui-bg-elevated)] hover:text-[var(--ui-text)]'"
                    @click="onNavigate"
                  >
                    <UIcon :name="child.icon" class="text-sm shrink-0" />
                    <span>{{ child.label }}</span>
                  </NuxtLink>
                </li>
              </ul>
            </template>
          </li>
        </ul>
      </div>
    </div>
  </nav>
</template>
